/**
 * Duplicate Role Dialog (ORCH-333, ORCH-21 §3.9, §11)
 *
 * Purpose: Names the copy of a role made into the active tenant. The copy is
 *          independent of its source; the server drops or caps what the tenant
 *          can't hold and reports it, which the caller shows.
 */

import React, { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { LoadingButton, DescriptionField } from '../../../components/common';
import { authzService, roleErrorMessage } from '../../../services/authzService';
import { Role, RoleAdjustment } from '../../../types/authz';
import { SecondaryButton } from '../../../components/common/ActionButtons';

interface DuplicateRoleDialogProps {
  role: Role;
  /** Role names already used in the active tenant (case-insensitive). */
  existingNames: string[];
  onClose: () => void;
  onDuplicated: (role: Role, adjusted: RoleAdjustment[]) => void;
}

const DuplicateRoleDialog: React.FC<DuplicateRoleDialogProps> = ({
  role,
  existingNames,
  onClose,
  onDuplicated,
}) => {
  const [name, setName] = useState(`${role.name} (copy)`);
  const [description, setDescription] = useState(role.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const taken = existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase());

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await authzService.duplicateRole(role.role_id, trimmed, description);
      onDuplicated(result.role, result.adjusted);
    } catch (e) {
      setError(roleErrorMessage(e, 'The role could not be duplicated'));
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-testid="duplicate-role-dialog">
      <DialogTitle>Duplicate {role.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          The copy belongs to this tenant and doesn't change when {role.name} does.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          error={taken}
          helperText={taken ? 'A role with this name already exists here' : ' '}
          inputProps={{ maxLength: 100, 'data-testid': 'duplicate-role-name' }}
        />
        <DescriptionField label="Description" value={description} onChange={setDescription} />
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose} disabled={saving}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={!trimmed || taken}
          data-testid="duplicate-role-save"
        >
          Duplicate
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateRoleDialog;
